/**
 * <ma-lightbox> — full-screen product image viewer for the PDP hero.
 * Replaces Dawn's stacked-image modal for image media (video / 3D still use Dawn's).
 *  - opens with the tapped gallery image flying up into place (and back down on close)
 *  - pinch to zoom (up to 4×) with the fingers, drag to pan with momentum, rubber-band at the edges
 *  - double-tap (touch) or click (mouse) zooms 2.5× at that point / back out; ctrl+wheel or trackpad pinch zooms too
 *  - swipe / drag / ←→ / arrows / thumbnails to move; swipe down to close
 *  - Esc / close button / backdrop closes; focus returns to the opener; page scroll is locked
 * Images come from the <script type="application/json" data-lightbox-media> the section renders.
 */
if (!customElements.get('ma-lightbox')) {
  const EASE = 'cubic-bezier(.32,.72,0,1)'; // iOS sheet curve
  const MAX = 4;
  const DBL = 2.5;
  const clamp = (v, a, b) => Math.min(b, Math.max(a, v));
  // past `lim` the value moves less and less, like iOS overscroll
  const rubber = (v, lim, dim) => {
    const o = Math.abs(v) - lim;
    return o <= 0 ? v : Math.sign(v) * (lim + dim * (1 - 1 / ((o * 0.55) / dim + 1)));
  };

  class MaLightbox extends HTMLElement {
    connectedCallback() {
      const data = this.querySelector('[data-lightbox-media]');
      this.media = data ? JSON.parse(data.textContent) : [];
      if (!this.media.length) return;
      this.reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.scope = this.closest('product-info') || document;
      this.build();
      // hijack Dawn's zoom buttons for image media only
      this.scope.addEventListener('click', (e) => {
        const btn = e.target.closest('.product__media-toggle');
        if (!btn) return;
        const item = btn.closest('[data-media-id]');
        const id = item && item.dataset.mediaId.split('-').pop();
        const i = this.media.findIndex((m) => String(m.id) === id);
        if (i < 0) return; // not an image → Dawn's modal
        e.preventDefault();
        e.stopImmediatePropagation();
        this.open(i, btn);
      }, true);
    }

    build() {
      const esc = (s) => String(s || '').replace(/"/g, '&quot;');
      const touch = window.matchMedia('(pointer: coarse)').matches;
      this.innerHTML += `
        <div class="ma-lightbox" role="dialog" aria-modal="true" aria-label="Product images" hidden>
          <div class="ma-lightbox__bg"></div>
          <div class="ma-lightbox__bar">
            <span class="ma-lightbox__count" aria-live="polite"></span>
            <button type="button" class="ma-lightbox__close" aria-label="Close">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"><path d="M6 6l12 12M18 6 6 18"/></svg>
            </button>
          </div>
          <div class="ma-lightbox__track">
            ${this.media.map((m, i) => `
              <figure class="ma-lightbox__slide" data-i="${i}">
                <img src="${m.src}" srcset="${m.srcset}" sizes="100vw" alt="${esc(m.alt)}" width="${m.w}" height="${m.h}" loading="${i ? 'lazy' : 'eager'}" draggable="false">
              </figure>`).join('')}
          </div>
          <button type="button" class="ma-lightbox__nav ma-lightbox__nav--prev" aria-label="Previous image"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="m15 18-6-6 6-6"/></svg></button>
          <button type="button" class="ma-lightbox__nav ma-lightbox__nav--next" aria-label="Next image"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="m9 18 6-6-6-6"/></svg></button>
          <div class="ma-lightbox__thumbs" role="tablist" aria-label="Choose image">
            ${this.media.map((m, i) => `<button type="button" role="tab" class="ma-lightbox__thumb" data-i="${i}" aria-label="Image ${i + 1}"><img src="${m.thumb}" alt="" loading="lazy" draggable="false"></button>`).join('')}
          </div>
          <p class="ma-lightbox__hint">${touch ? 'Pinch or double-tap to zoom' : 'Click or pinch to zoom &middot; drag to move'}</p>
        </div>`;
      this.el = this.querySelector('.ma-lightbox');
      this.track = this.el.querySelector('.ma-lightbox__track');
      this.slides = [...this.el.querySelectorAll('.ma-lightbox__slide')];
      this.thumbs = [...this.el.querySelectorAll('.ma-lightbox__thumb')];
      this.count = this.el.querySelector('.ma-lightbox__count');
      // everything but the image fades with open / close / swipe-down
      this.chrome = [...this.el.querySelectorAll('.ma-lightbox__bg, .ma-lightbox__bar, .ma-lightbox__nav, .ma-lightbox__thumbs, .ma-lightbox__hint')];

      this.el.querySelector('.ma-lightbox__close').addEventListener('click', () => this.close());
      this.el.querySelector('.ma-lightbox__nav--prev').addEventListener('click', () => this.step(-1));
      this.el.querySelector('.ma-lightbox__nav--next').addEventListener('click', () => this.step(1));
      window.MaLoop && window.MaLoop.edges(this.track, { next: () => this.step(1), prev: () => this.step(-1) });
      this.thumbs.forEach((t) => t.addEventListener('click', () => this.go(+t.dataset.i)));
      this.track.addEventListener('scroll', () => {
        cancelAnimationFrame(this._raf);
        this._raf = requestAnimationFrame(() => {
          const i = Math.round(this.track.scrollLeft / this.track.clientWidth);
          if (i !== this.i && this.slides[i]) this.setIndex(i);
        });
      }, { passive: true });
      this.el.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') this.close();
        else if (e.key === 'ArrowRight') this.step(1);
        else if (e.key === 'ArrowLeft') this.step(-1);
        else if (e.key === 'Tab') this.trap(e);
      });
      this.el.addEventListener('gesturestart', (e) => e.preventDefault()); // iOS: no page zoom underneath
      window.addEventListener('popstate', () => this.close(true));
      // rotation / window resize: keep the current slide exactly in frame
      let w = window.innerWidth;
      window.addEventListener('resize', () => {
        if (this.el.hidden || window.innerWidth === w) return; // mobile URL bar showing / hiding only changes height
        w = window.innerWidth;
        this.reset(0);
        this.track.scrollLeft = this.slides[this.i].offsetLeft;
      });
      this.gestures();
    }

    // ---------- zoom state of the current image: translate(x, y) scale(s), origin = centre ----------
    apply(ms) {
      const z = this.z;
      z.img.style.transition = ms && !this.reduce ? `transform ${ms}ms ${EASE}` : 'none';
      z.img.style.transform = z.s === 1 && !z.x && !z.y ? '' : `translate3d(${z.x}px, ${z.y}px, 0) scale(${z.s})`;
      this.el.classList.toggle('is-zoomed', z.s > 1.01);
    }
    // keep the image point under (px, py) fixed while scaling to s
    zoomTo(s, px, py) {
      const z = this.z;
      const r = z.img.parentElement.getBoundingClientRect();
      const cx = px - r.left - r.width / 2;
      const cy = py - r.top - r.height / 2;
      z.x = cx - ((cx - z.x) * s) / z.s;
      z.y = cy - ((cy - z.y) * s) / z.s;
      z.s = s;
    }
    bounds(s) {
      const img = this.z.img;
      const box = img.parentElement;
      return {
        bx: Math.max(0, (img.offsetWidth * s - box.clientWidth) / 2),
        by: Math.max(0, (img.offsetHeight * s - box.clientHeight) / 2),
        w: box.clientWidth,
        h: box.clientHeight,
      };
    }
    clampXY() {
      const z = this.z;
      const { bx, by } = this.bounds(z.s);
      z.x = clamp(z.x, -bx, bx);
      z.y = clamp(z.y, -by, by);
    }
    settle(px, py) {
      const z = this.z;
      if (z.s <= 1) Object.assign(z, { s: 1, x: 0, y: 0 });
      else if (z.s > MAX) this.zoomTo(MAX, px, py);
      this.clampXY();
      this.apply(450);
    }
    reset(ms) {
      if (!this.z || (this.z.s === 1 && !this.z.x && !this.z.y)) return;
      Object.assign(this.z, { s: 1, x: 0, y: 0 });
      this.apply(ms);
    }
    toggleZoom(px, py) {
      if (this.z.s > 1.01) return this.reset(450);
      this.zoomTo(DBL, px, py);
      this.clampXY();
      this.apply(450);
    }
    // catch an image that is still gliding, so a new touch picks it up where it is
    freeze() {
      const z = this.z;
      const m = new DOMMatrixReadOnly(getComputedStyle(z.img).transform === 'none' ? undefined : getComputedStyle(z.img).transform);
      Object.assign(z, { s: m.a, x: m.e, y: m.f });
      this.apply(0);
    }
    fade(a) {
      this.chrome.forEach((c) => (c.style.opacity = a === 1 ? '' : a));
    }

    gestures() {
      const t = this.track;
      const P = new Map();
      let mode = null;
      let start, last, vel, pinch;
      const pts = () => [...P.values()];
      const mid = () => {
        const [a, b] = pts();
        return { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2, d: Math.hypot(a.x - b.x, a.y - b.y) || 1 };
      };
      const begin = (x, y, e) => {
        start = { x, y, sl: t.scrollLeft, zx: this.z.x, zy: this.z.y, i: this.i, onImg: e ? e.target === this.z.img : false };
        last = { x, y, t: performance.now() };
        vel = { x: 0, y: 0 };
      };
      const startPinch = () => {
        const m = mid();
        const r = this.z.img.parentElement.getBoundingClientRect();
        pinch = { d: m.d, s: this.z.s, cx: m.x - r.left - r.width / 2, cy: m.y - r.top - r.height / 2, zx: this.z.x, zy: this.z.y, r };
        mode = 'pinch';
      };

      t.addEventListener('pointerdown', (e) => {
        if (this.flying || e.button > 0) return;
        t.setPointerCapture(e.pointerId);
        P.set(e.pointerId, { x: e.clientX, y: e.clientY });
        cancelAnimationFrame(this._sa);
        t.classList.remove('is-dragging');
        if (P.size === 1) {
          this.freeze();
          begin(e.clientX, e.clientY, e);
          mode = 'pending';
        } else if (P.size === 2 && (mode === 'pending' || mode === 'pan')) {
          this.freeze();
          startPinch();
        }
      });

      t.addEventListener('pointermove', (e) => {
        if (!P.has(e.pointerId)) return;
        P.set(e.pointerId, { x: e.clientX, y: e.clientY });
        const z = this.z;
        if (mode === 'pinch') {
          if (P.size < 2) return;
          const m = mid();
          let s = (pinch.s * m.d) / pinch.d;
          // resist past the limits (tops out at 1.5× MAX / 0.5×), then settle() eases back
          if (s > MAX) { const o = s / MAX - 1; s = MAX * (1 + (0.5 * o) / (1 + o)); }
          else if (s < 1) { const u = 1 / s - 1; s = 1 - (0.5 * u) / (1 + u); }
          // the image point that started under the fingers' midpoint stays under it
          const cx = m.x - pinch.r.left - pinch.r.width / 2;
          const cy = m.y - pinch.r.top - pinch.r.height / 2;
          z.x = cx - ((pinch.cx - pinch.zx) * s) / pinch.s;
          z.y = cy - ((pinch.cy - pinch.zy) * s) / pinch.s;
          z.s = s;
          this.apply(0);
          return;
        }
        if (P.size !== 1 || !mode) return;
        const now = performance.now();
        const dt = now - last.t;
        if (dt > 0) {
          vel = { x: ((e.clientX - last.x) / dt) * 0.8 + vel.x * 0.2, y: ((e.clientY - last.y) / dt) * 0.8 + vel.y * 0.2 };
          last = { x: e.clientX, y: e.clientY, t: now };
        }
        const dx = e.clientX - start.x;
        const dy = e.clientY - start.y;
        if (mode === 'pending') {
          if (Math.hypot(dx, dy) < 6) return;
          mode = z.s > 1.01 ? 'pan' : Math.abs(dx) > Math.abs(dy) ? 'swipe' : 'dismiss';
          if (mode === 'swipe' && this.slides.length < 2) mode = 'dismiss';
          if (mode === 'swipe') t.classList.add('is-dragging');
          if (mode === 'dismiss') this.el.classList.add('is-dragging');
        }
        if (mode === 'swipe') {
          t.scrollLeft = start.sl - dx;
        } else if (mode === 'pan') {
          const b = this.bounds(z.s);
          z.x = rubber(start.zx + dx, b.bx, b.w);
          z.y = rubber(start.zy + dy, b.by, b.h);
          this.apply(0);
        } else if (mode === 'dismiss') {
          const p = Math.min(1, Math.abs(dy) / (window.innerHeight * 0.6));
          Object.assign(z, { x: dx * 0.6, y: dy, s: 1 - p * 0.3 });
          this.apply(0);
          this.fade(1 - p);
        }
      });

      const up = (e) => {
        if (!P.has(e.pointerId)) return;
        P.delete(e.pointerId);
        const z = this.z;
        if (mode === 'pinch') {
          const m = pts()[0] || { x: e.clientX, y: e.clientY };
          if (P.size === 1 && z.s >= 1 && z.s <= MAX) {
            // one finger left: carry on panning with it
            mode = z.s > 1.01 ? 'pan' : null;
            begin(m.x, m.y);
          } else if (P.size <= 1) {
            this.settle(pinch.cx + pinch.r.left + pinch.r.width / 2, pinch.cy + pinch.r.top + pinch.r.height / 2);
            mode = null;
          }
          return;
        }
        if (P.size) return;
        const dx = e.clientX - start.x;
        const dy = e.clientY - start.y;
        if (mode === 'pan') {
          // pulled well past the side of a zoomed image: carry on to the neighbour, like Photos
          const b = this.bounds(z.s);
          const rx = start.zx + dx;
          const over = rx > b.bx ? rx - b.bx : rx < -b.bx ? rx + b.bx : 0;
          if (this.slides.length > 1 && Math.abs(over) > 70 && Math.abs(dx) > Math.abs(dy)) {
            mode = null;
            return this.step(over > 0 ? -1 : 1);
          }
          if (performance.now() - last.t > 80) vel = { x: 0, y: 0 }; // finger rested: no fling
          z.x += vel.x * 260;
          z.y += vel.y * 260;
          this.clampXY();
          this.apply(620);
        } else if (mode === 'swipe') {
          t.classList.remove('is-dragging');
          const w = t.clientWidth;
          const d = dx < -w * 0.2 || (vel.x < -0.35 && dx < -20) ? 1 : dx > w * 0.2 || (vel.x > 0.35 && dx > 20) ? -1 : 0;
          this.setIndex(start.i);
          d ? this.step(d) : this.go(start.i);
        } else if (mode === 'dismiss') {
          this.el.classList.remove('is-dragging');
          if (Math.abs(dy) > 110 || Math.abs(vel.y) > 0.6) this.close();
          else {
            Object.assign(z, { s: 1, x: 0, y: 0 });
            this.apply(450);
            this.fade(1);
          }
        } else if (mode === 'pending' && e.type === 'pointerup') {
          this.tap(e, start.onImg);
        }
        mode = null;
      };
      t.addEventListener('pointerup', up);
      t.addEventListener('pointercancel', up);

      // trackpad pinch / ctrl+wheel zooms at the cursor; plain wheel pans while zoomed
      t.addEventListener('wheel', (e) => {
        if (this.flying) return;
        const z = this.z;
        if (e.ctrlKey) {
          e.preventDefault();
          const big = Math.abs(e.deltaY) > 40; // mouse wheel notch vs trackpad
          this.zoomTo(clamp(z.s * Math.exp(-e.deltaY * (big ? 0.004 : 0.012)), 1, MAX), e.clientX, e.clientY);
          this.clampXY();
          this.apply(big ? 260 : 0);
        } else if (z.s > 1.01) {
          e.preventDefault();
          z.x -= e.deltaX;
          z.y -= e.deltaY;
          this.clampXY();
          this.apply(0);
        }
      }, { passive: false });
    }

    tap(e, onImg) {
      const x = e.clientX;
      const y = e.clientY;
      if (e.pointerType === 'mouse') {
        if (onImg) this.toggleZoom(x, y);
        else if (this.z.s > 1.01) this.reset(450);
        else this.close();
        return;
      }
      const now = performance.now();
      if (this._tap && now - this._tap.t < 300 && Math.hypot(x - this._tap.x, y - this._tap.y) < 40) {
        clearTimeout(this._tapT);
        this._tap = null;
        return this.toggleZoom(x, y);
      }
      this._tap = { t: now, x, y };
      // single tap: on the image it hides / shows the controls (like Photos); on the backdrop it closes
      this._tapT = setTimeout(() => {
        this._tap = null;
        onImg || this.z.s > 1.01 ? this.el.classList.toggle('is-bare') : this.close();
      }, 300);
    }

    // ---------- open / close: the image flies between the page gallery and the viewer ----------
    // gallery <img> for media i, if it is on screen (scrolls the gallery to it first when asked)
    source(i, sync) {
      const li = this.scope.querySelector(`.product__media-item[data-media-id$="-${this.media[i].id}"]`);
      const img = li && li.querySelector('img');
      if (!img || !li.offsetWidth) return null;
      const list = li.parentElement;
      if (sync && list.scrollWidth > list.clientWidth + 2) {
        // the gallery has scroll-behavior: smooth, so jump explicitly; a glide would move the
        // landing spot from under the flying image and leave the gallery between two slides
        // and aim at the slide's real snap point: Dawn's mobile slider has a 15px scroll-padding, so offsetLeft
        // would sit 15px past it and the browser would twitch it back on the next touch
        const first = [...list.children].find((c) => c.offsetWidth);
        const left = window.MaLoop ? window.MaLoop.snapLeft(list, li) : li.offsetLeft - first.offsetLeft;
        list.scrollTo({ left, behavior: 'instant' });
        // the gallery fades / shrinks off-centre slides on scroll; this one is centre now
        if (li.firstElementChild) li.firstElementChild.style.transform = li.firstElementChild.style.opacity = '';
      }
      const r = img.getBoundingClientRect();
      const lr = list.getBoundingClientRect();
      const cx = r.left + r.width / 2;
      const cy = r.top + r.height / 2;
      const seen = cx > lr.left && cx < lr.right && cy > 0 && cy < window.innerHeight;
      return seen && r.width ? img : null;
    }
    // ghost copy of `a` flies onto `b`'s box; `b` is shown once it has decoded
    fly(a, b, ms) {
      const ra = a.getBoundingClientRect();
      const rb = b.getBoundingClientRect();
      const k = ra.width / rb.width;
      const g = document.createElement('img');
      g.className = 'ma-lightbox__ghost';
      g.alt = '';
      g.src = a.currentSrc || a.src;
      const rad = getComputedStyle(b).borderTopLeftRadius;
      Object.assign(g.style, { left: `${rb.left}px`, top: `${rb.top}px`, width: `${rb.width}px`, height: `${rb.height}px`, borderRadius: rad });
      document.body.append(g);
      a.style.visibility = b.style.visibility = 'hidden';
      this.flying = true;
      const from = `translate(${ra.left - rb.left}px, ${ra.top - rb.top}px) scale(${k}, ${ra.height / rb.height})`;
      const fromRad = `${parseFloat(getComputedStyle(a).borderTopLeftRadius) / k || 0}px`;
      return g
        .animate([{ transform: from, borderRadius: fromRad }, { transform: 'none', borderRadius: rad }], { duration: ms, easing: EASE })
        // a lazy image that hasn't loaded keeps the ghost over it until it can show (1.5 s at most)
        .finished.then(() => (b.complete && b.naturalWidth ? null : Promise.race([b.decode().catch(() => {}), new Promise((r) => setTimeout(r, 1500))])))
        .then(() => {
          b.style.visibility = '';
          requestAnimationFrame(() => g.remove());
          this.flying = false;
        });
    }

    open(i, opener) {
      this.opener = opener;
      this.el.classList.remove('is-bare');
      this.el.hidden = false;
      document.documentElement.classList.add('ma-lightbox-open');
      this.track.scrollLeft = this.slides[i].offsetLeft;
      this.setIndex(i);
      this.el.querySelector('.ma-lightbox__close').focus({ preventScroll: true });
      // phones: the back button / gesture should close the viewer, not leave the page
      if (!(history.state && history.state.maLightbox)) history.pushState({ ...history.state, maLightbox: true }, '');
      this._closeAfterFly = false;
      if (this.reduce) return;
      const ms = 480;
      this.chrome.forEach((c) => c.animate([{ opacity: 0 }, { opacity: 1 }], { duration: ms * 0.8, easing: 'ease-out' }));
      const src = this.source(i);
      const landed = () => this._closeAfterFly && this.close();
      if (src) this.fly(src, this.z.img, ms).then(() => { src.style.visibility = ''; landed(); });
      else this.z.img.animate([{ opacity: 0, transform: 'scale(.92)' }, { opacity: 1, transform: 'none' }], { duration: ms, easing: EASE });
    }
    close(fromHistory) {
      if (this.closing || this.el.hidden) return;
      if (this.flying) return void (this._closeAfterFly = true); // still flying in: close as soon as it lands
      this.closing = true;
      // the back button closes the viewer (see open); closing any other way drops that history entry
      if (!fromHistory && history.state && history.state.maLightbox) history.back();
      clearTimeout(this._tapT);
      const img = this.z.img;
      const done = () => {
        this.el.hidden = true;
        this.chrome.forEach((c) => c.getAnimations().forEach((a) => a.cancel()));
        this.fade(1);
        Object.assign(this.z, { s: 1, x: 0, y: 0 });
        this.apply(0);
        img.style.visibility = '';
        document.documentElement.classList.remove('ma-lightbox-open');
        this.closing = false;
        this.opener && this.opener.focus({ preventScroll: true });
      };
      if (this.reduce) return done();
      const ms = 420;
      this.chrome.forEach((c) => c.animate([{ opacity: 0 }], { duration: ms * 0.75, easing: 'ease-out', fill: 'forwards' }));
      const dst = this.source(this.i, true);
      if (dst) this.fly(img, dst, ms).then(done);
      else img.animate([{ opacity: 0, transform: `${getComputedStyle(img).transform.replace('none', '')} scale(.9)` }], { duration: ms * 0.7, easing: EASE, fill: 'forwards' }).finished.then(() => { img.getAnimations().forEach((a) => a.cancel()); done(); });
    }

    // ---------- slides ----------
    // endless: past either end, dissolve round to the other end with the loop cue
    step(d) {
      const n = this.slides.length;
      const j = this.i + d;
      if (j >= 0 && j < n) return this.go(j);
      const k = j < 0 ? n - 1 : 0;
      if (!window.MaLoop || n < 2) return this.go(k);
      this.reset(0);
      window.MaLoop.jump(this.track, { left: this.slides[k].offsetLeft, host: this.track.parentElement, text: `${k + 1} / ${n}` });
      this.setIndex(k);
    }
    go(i) {
      i = clamp(i, 0, this.slides.length - 1);
      this.reset(300);
      this.scrollToX(this.slides[i].offsetLeft);
      this.setIndex(i);
    }
    // eased scroll so every slide change glides on the same curve (native smooth scroll varies by browser)
    scrollToX(left) {
      const t = this.track;
      const from = t.scrollLeft;
      const d = left - from;
      cancelAnimationFrame(this._sa);
      if (!d) return;
      if (this.reduce) return void (t.scrollLeft = left);
      const ms = Math.min(560, 340 + Math.abs(d) * 0.12);
      const t0 = performance.now();
      t.classList.add('is-dragging'); // snap off while gliding
      const tick = (now) => {
        const p = Math.min(1, (now - t0) / ms);
        t.scrollLeft = from + d * (p === 1 ? 1 : 1 - Math.pow(2, -10 * p));
        if (p < 1) this._sa = requestAnimationFrame(tick);
        else t.classList.remove('is-dragging');
      };
      this._sa = requestAnimationFrame(tick);
    }
    setIndex(i) {
      const img = this.slides[i].querySelector('img');
      if (!this.z || this.z.img !== img) {
        if (this.z && this.z.img.style.transform) this.z.img.style.transform = this.z.img.style.transition = '';
        this.z = { img, s: 1, x: 0, y: 0 };
        this.el.classList.remove('is-zoomed');
      }
      this.i = i;
      // neighbours load now, so a swipe never lands on a blank slide
      [i - 1, i + 1].forEach((k) => { const s = this.slides[k]; if (s) s.querySelector('img').loading = 'eager'; });
      this.count.textContent = `${i + 1} / ${this.slides.length}`;
      this.thumbs.forEach((t, j) => t.setAttribute('aria-selected', String(j === i)));
      const t = this.thumbs[i];
      t && t.scrollIntoView({ block: 'nearest', inline: 'center', behavior: this.reduce ? 'auto' : 'smooth' });
      this.el.querySelectorAll('.ma-lightbox__nav').forEach((b) => (b.disabled = this.slides.length < 2));
    }
    trap(e) {
      const f = [...this.el.querySelectorAll('button:not([disabled])')];
      if (!f.length) return;
      const first = f[0], last = f[f.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    }
  }
  customElements.define('ma-lightbox', MaLightbox);
}
